import {CSSProperties, useEffect, useState} from 'react'
import _ from 'lodash'
import {Checkbox, Popup} from 'semantic-ui-react'
import Slider from 'react-slider'
import {BiCut, BiEdit, BiTrash} from 'react-icons/bi'
import {IFieldDescriptor, InitViewTemplates} from '@/features/mixer'
import {CutControl} from './cut'
import {defaultCutStepSize} from '../../semantic-filtering/const'
import './style.css'

export interface IMixerChannelData {
  isInitialized: boolean
  initParams?: any
  sort: number
  cut?: {
    min: number
    max: number
  }
  // range: {
  //     min: number,
  //     max: number,
  // },
  mute?: boolean
  solo?: boolean
}

export interface ISimplifiedMixerChannelData {
  isSort: boolean
  isRequired: boolean
  isExcluded: boolean
}

export interface IMixerChannel {
  descriptor: IFieldDescriptor
  value: IMixerChannelData
  stepSize?: number
  cutStepSize?: number
  isInteger?: boolean
}

export interface IMixerChannelProps extends IMixerChannel {
  disabled?: boolean
  simplified?: boolean
  showMute?: boolean
  showSolo?: boolean
  showReInit?: boolean
  disableMute?: boolean
  disableSolo?: boolean
  canSort?: boolean
  canCut?: boolean
  style?: CSSProperties
  onChanged: (
    args: Partial<{
      descriptor: Partial<IFieldDescriptor>
      data: Partial<IMixerChannelData>
    }>
  ) => void
  onDelete?: () => void
}

export function MixerChannel(props: IMixerChannelProps) {
  const {
    descriptor,
    value,
    disabled,
    simplified,
    showMute,
    showSolo,
    showReInit,
    disableMute,
    disableSolo,
    canSort,
    canCut,
    stepSize,
    cutStepSize,
    isInteger,
    style,
    onChanged,
    onDelete,
  } = {
    showMute: true,
    showSolo: true,
    showReInit: true,
    canSort: true,
    canCut: true,
    stepSize: 0.01,
    cutStepSize: props.isInteger ? 1 : props.cutStepSize || defaultCutStepSize,
    ...props,
  }

  const {label, initView, range} = descriptor

  const {isInitialized, sort, cut, mute, solo} = value

  const [simplifiedState, setSimplifiedState] = useState<ISimplifiedMixerChannelData>({
    isExcluded: false,
    isRequired: false,
    isSort: false,
  })

  useEffect(() => {
    setSimplifiedState({
      isSort: sort !== 0,
      isRequired: cut !== undefined && (cut.min !== 0 || cut.max !== 0),
      isExcluded: cut !== undefined && cut.min === 0 && cut.max === 0,
    })
  }, [sort, cut])

  function handleOnCutClick() {
    onChanged(
      cut !== undefined ?
        {data: {cut: undefined}}
      : {
          data: {
            cut: {min: range?.min || 0, max: range?.max || 1},
          },
        }
    )
  }

  function handleZeroSort() {
    onChanged({data: {sort: 0}})
  }

  // function handleZeroCutMin()
  // {
  //     onChanged({
  //         cut: {min: 0, max: cut?.max || 0},
  //     });
  // }
  // function handleZeroCutMax()
  // {
  //     onChanged({
  //         cut: {min: cut?.min || 0, max: 0},
  //     });
  // }

  function handleNudgeSort(value: number) {
    const newVal = _.clamp(sort + value, -1, 1)
    onChanged({data: {sort: Math.abs(newVal) >= stepSize ? newVal : 0}})
  }

  function handleInitFinalize(descriptor: IFieldDescriptor, initParams: any) {
    onChanged({
      descriptor,
      data: {
        isInitialized: true,
        initParams,
      },
    })
  }

  function handleSimplifiedPatchData(patchData: Partial<ISimplifiedMixerChannelData>) {
    if (patchData.isExcluded) {
      patchData.isRequired = false
      patchData.isSort = false
    } else {
      patchData.isSort = true
    }
    if (patchData.isRequired) {
      patchData.isExcluded = false
    }
    if (patchData.isSort) {
      patchData.isExcluded = false
    }

    patchData = {
      ...simplifiedState,
      ...patchData,
    }

    // create diff state
    const diff: Partial<IMixerChannelData> = {
      mute: false,
      solo: false,
      sort: patchData.isSort ? 1 : 0,
    }
    if (patchData.isExcluded) {
      diff.cut = {
        min: 0,
        max: 0,
      }
    } else if (patchData.isRequired) {
      diff.cut = {
        min: range.min + cutStepSize,
        max: range.max,
      }
    } else diff.cut = undefined

    onChanged({data: diff})
  }

  const {isRequired, isExcluded} = simplifiedState

  const classNames = _.chain([
    'channel',
    simplified ? 'simplified' : undefined,
    disabled ? 'disabled' : undefined,
    mute ? 'muted' : undefined,
    disableMute ? 'mute-disabled' : undefined,
    disableSolo ? 'solo-disabled' : undefined,
  ])
    .compact()
    .join(' ')
    .value()

  const InitView = initView ? InitViewTemplates[initView] : undefined

  return (
    <div
      className={classNames}
      style={style}
    >
      <div className='label'>
        <div className='text'>{label}</div>
        <div className='actions'>
          {isInitialized && initView && showReInit ?
            <Popup
              size='tiny'
              content={'edit'}
              trigger={
                <button
                  className={'blue'}
                  onClick={() => onChanged({data: {isInitialized: false}})}
                  children={<BiEdit />}
                />
              }
            />
          : null}
          {isInitialized && showMute ?
            <Popup
              size='tiny'
              content={`${mute ? 'unmute' : 'mute'} channel`}
              trigger={
                <button
                  className={mute ? 'mute active blue' : 'mute blue'}
                  onClick={() => onChanged({data: {mute: !mute}})}
                  children='M'
                />
              }
            />
          : null}
          {isInitialized && showSolo ?
            <Popup
              size='tiny'
              content='solo channel'
              trigger={
                <button
                  className={solo ? 'solo active blue' : 'solo blue'}
                  onClick={() => onChanged({data: {solo: !solo}})}
                  children='S'
                />
              }
            />
          : null}
          {onDelete ?
            <Popup
              size='tiny'
              content='delete channel'
              trigger={
                <button
                  className='red'
                  children={<BiTrash />}
                  onClick={() => onDelete()}
                />
              }
            />
          : null}
        </div>
      </div>
      {InitView && !isInitialized ?
        <div className='controls init'>
          <InitView
            descriptor={descriptor}
            finalize={handleInitFinalize}
            data={value}
          />
        </div>
      : simplified ?
        <>
          {/* SIMPLIFIED */}
          <div
            className={`background ${
              isExcluded ? 'red'
              : isRequired ? 'green'
              : ''
            }`}
          />
          <div className='controls'>
            <div className='actions'>
              {/* <Checkbox
                        label='sort'
                        checked={isSort && !isExcluded}
                        disabled={isExcluded}
                        onChange={(_e, {checked}) => handleSimplifiedPatchData({isSort: checked})}
                    /> */}
              <Checkbox
                label='require'
                checked={isRequired}
                onChange={(_e, {checked}) => handleSimplifiedPatchData({isRequired: checked})}
              />
              <Checkbox
                label='exclude'
                checked={isExcluded}
                onChange={(_e, {checked}) => handleSimplifiedPatchData({isExcluded: checked})}
              />
            </div>
          </div>
          {isRequired ?
            <CutControl
              isInteger={isInteger}
              descriptor={descriptor}
              value={value}
              onChanged={onChanged}
            />
          : null}
        </>
      : <>
          {/* STANDARD */}
          {canSort ?
            <div className='controls sort'>
              <div className='background'>
                <div className='area left'>
                  <div
                    className='nudge'
                    onClick={() => handleNudgeSort(-stepSize)}
                  >
                    less
                  </div>
                </div>
                <div className='area right'>
                  <div
                    className='nudge'
                    onClick={() => handleNudgeSort(stepSize)}
                  >
                    more
                  </div>
                </div>
                <div className='mark-middle' />
              </div>
              <div
                className='label'
                style={{
                  display: 'flex',
                  justifyContent: sort < 0 ? 'start' : 'end',
                }}
              >
                sort * {sort.toFixed(2)}
              </div>
              <div className='slider-wrap'>
                <div
                  className='nudge'
                  onClick={() => handleNudgeSort(-stepSize)}
                >
                  -
                </div>
                <Popup
                  basic
                  size='tiny'
                  content='set sort influence to zero'
                  trigger={
                    <div
                      className='zero'
                      onClick={() => handleZeroSort()}
                      children={'0'}
                    />
                  }
                />
                <Slider
                  min={-1}
                  max={1}
                  step={stepSize}
                  value={sort}
                  onAfterChange={(value) => onChanged({data: {sort: value}})}
                  disabled={disabled}
                />
                <div
                  className='nudge'
                  onClick={() => handleNudgeSort(stepSize)}
                >
                  +
                </div>
              </div>
              <div className='actions'>
                <Popup
                  basic
                  size='tiny'
                  content={cut === undefined ? 'enable cut' : 'disable cut'}
                  trigger={
                    <button
                      className={cut !== undefined ? 'red active' : 'red'}
                      children={<BiCut />}
                      onClick={handleOnCutClick}
                    />
                  }
                />
              </div>
            </div>
          : null}
          {(canCut || !canSort) && cut !== undefined ?
            <CutControl
              cutStepSize={cutStepSize}
              isInteger={isInteger}
              disabled={disabled}
              descriptor={descriptor}
              value={value}
              onChanged={onChanged}
            />
          : null}
        </>
      }
    </div>
  )
}
